/**
 * Keyboard Focus Trap
 * -----------------------------------------------------------------------------
 *
 * Identifies all focusable elements inside the provided container and ensures
 * that keyboard users cannot tab out of it.
 *
 * Focus is moved to the first focusable element after initialization.
 *
 * @param {DOMNode} container The container element which focus should be
 *                            trapped. The element should contain one or more
 *                            naturally focusable elements or elements with a
 *                            valid `tabindex` value.
 * @param {Object} options
 * @param {Function} options.onDeactivate  Called when the focus trap is deactivated.
 * @param {DOMNode} options.returnFocusEl  Element to restore focus to when the trap is deactivated.
 * @return {Function}                      Untrap function.
 */
export default function trapFocus( container, options = {} ) {

  const {
    onDeactivate  = () => {},
    returnFocusEl = document.activeElement
  } = options;
  const focusableSelectors = 'a[href], button, textarea, input, select, summary, [tabindex]:not([tabindex="-1"])';
  
  const getFocusableElements = () => Array.from( container.querySelectorAll( focusableSelectors ) ).filter( ( el ) => {
    if ( el.hasAttribute( 'disabled' ) ) {
      return false;
    }

    if ( el.getAttribute( 'aria-hidden' ) === 'true' ) {
      return false;
    }

    const style = window.getComputedStyle( el );
    if ( 'none' === style.display || 'hidden' === style.visibility ) {
      return false;
    }

    // Hidden in layout (except fixed-positioned items)
    if ( null === el.offsetParent && 'fixed' !== style.position ) {
      return false;
    }

    return true;
  } );

  const focusFirst = () => {
    const elements = getFocusableElements();
    if ( elements.length ) {
      elements[ 0 ].focus();
    }
  };

  // Ensure initial focus starts inside container
  focusFirst();

  const onKeydown = ( e ) => {

    if ( 'Escape' === e.key ) {
      onDeactivate();
      return;
    }

    if ( 'Tab' !== e.key ) {
      return;
    }

    const elements = getFocusableElements();
    if ( 0 === elements.length ) {
      e.preventDefault();
      return;
    }

    const first = elements[ 0 ];
    const last  = elements[ elements.length - 1 ];
    const active = document.activeElement;

    // If focus is already outside, bring it back in
    if ( !container.contains( active ) ) {
      e.preventDefault();
      first.focus();
      return;
    }

    // SHIFT+TAB on first → wrap to last
    if ( e.shiftKey && active === first ) {
      e.preventDefault();
      last.focus();
      return;
    }

    // TAB on last → wrap to first
    if ( !e.shiftKey && active === last ) {
      e.preventDefault();
      first.focus();
    }

  };

  const onFocusIn = ( e ) => {
    if ( !container.contains( e.target ) ) {
      focusFirst();
    }
  };

  // Use document listeners so trap still works reliably
  document.addEventListener( 'keydown', onKeydown );
  document.addEventListener( 'focusin', onFocusIn );

  return () => {
    document.removeEventListener( 'keydown', onKeydown );
    document.removeEventListener( 'focusin', onFocusIn );
    returnFocusEl.focus();
  };

}